import React, { memo, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import PeopleIcon from 'transfix-icons/build/PeopleIcon'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { StringParam } from 'use-query-params'
import { DriverSingleInlineEdit } from 'apps/fleet-planner/components/form-components/DriverSingleInlineEdit'
import { useShipmentApi } from 'apps/fleet-planner/hooks/useShipmentApi'
import { IInlineEditProps } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import { Tooltip } from 'transfix-ui'
import { useQueryParams } from 'utils/hooks'

const MAX_DRIVERS = 2

export const getDefaultInputCount = (drivers: (Types.IDriver | null)[] | null | undefined = []) => {
  const { length = 0 } = drivers || {}
  return length <= MAX_DRIVERS ? length : 1
}

interface IDriverSingleInlineEditProps extends IInlineEditProps<Types.ILoad['drivers']> {
  isExternalLoad?: boolean
}

export const DriversSingleInlineEdit = memo<IDriverSingleInlineEditProps>(
  ({
    currentEditField,
    isExternal,
    isExternalLoad,
    onEditModeRequested: onEditModeRequestedProp,
    value: valueProp,
  }) => {
    const history = useHistory()
    const { pathname = '' } = useLocation()
    const { updateShipmentDrivers } = useShipmentApi()
    const [inputCount, setInputCount] = useState<number>(getDefaultInputCount(valueProp))
    const [driver1, driver2] = valueProp ?? []
    const [{ modalMessageKey }] = useQueryParams({
      modalMessageKey: StringParam,
    })

    // Update state when props update
    useEffect(() => {
      setInputCount(getDefaultInputCount(valueProp))
    }, [valueProp])

    const selectedDrivers = valueProp?.filter(d => !!d && d !== null) || []

    const changeHandler = async (change: Types.IDriver | null | undefined, i: number) => {
      const newDrivers = [...selectedDrivers]

      if (change?.id) {
        newDrivers[i] = change
      } else {
        newDrivers.splice(i, 1)
      }

      const driverIds = newDrivers

        .filter(d => typeof d !== 'undefined' && d !== null)
        .map(d => d?.id)

      await updateShipmentDrivers(driverIds as string[])
      if (isExternalLoad && modalMessageKey)
        history.push(pathname.replace(/(\/?modalMessageKey=addDriverAlert)/, ''))
    }

    const removeDriverHandler = (driverIndex: number) => {
      changeHandler(undefined, driverIndex)
      setInputCount(prev => (prev > 1 ? prev - 1 : prev))
    }

    const addAnotherDriverHandler = () => {
      setInputCount(prev => prev + 1)
    }

    return (
      <div className='driver-inline-edit'>
        {/* Driver 1 */}
        <div className='flex items-end'>
          <div className='flex-auto'>
            <DriverSingleInlineEdit
              dataTest='driver-1-inline-edit'
              selectedDrivers={selectedDrivers}
              value={driver1}
              onCancel={() => {}}
              fieldIndex={0}
              onChange={change => changeHandler(change, 0)}
              currentEditField={currentEditField}
              onEditModeRequested={str => onEditModeRequestedProp(str)}
              onUpdate={() => {}}
              isExternal={isExternal}
              isExternalLoad={isExternalLoad}
            />
          </div>
        </div>

        {/* Driver 2 */}
        {inputCount > 1 && (
          <div className='flex items-end'>
            <div className='flex-auto'>
              <DriverSingleInlineEdit
                dataTest='driver-2-inline-edit'
                selectedDrivers={selectedDrivers}
                value={driver2}
                onCancel={() => {}}
                fieldIndex={1}
                onChange={change => changeHandler(change, 1)}
                currentEditField={currentEditField}
                onEditModeRequested={str => onEditModeRequestedProp(str)}
                onUpdate={() => {}}
                isExternalLoad={isExternalLoad}
              />
            </div>
            {!!driver2?.id &&
              selectedDrivers.length - 1 >= 1 &&
              !currentEditField?.includes(`driver-1`) && (
                <Tooltip
                  position='top'
                  type='dark'
                  effect='solid'
                  trigger={
                    <IconButton
                      className='mb5'
                      color='tertiary'
                      Icon={DeleteIcon}
                      onClick={() => removeDriverHandler(1)}
                      label='Remove driver'
                      data-test='remove-second-driver'
                      size='large'
                    />
                  }>
                  Remove Driver
                </Tooltip>
              )}
          </div>
        )}

        {selectedDrivers?.length === 1 && inputCount === 1 && (
          <Button
            className='mt4'
            startIcon={<PeopleIcon />}
            onClick={addAnotherDriverHandler}
            data-test='add-driver-2-button'>{`Add Driver ${
            (selectedDrivers?.length || 0) + 1
          }`}</Button>
        )}
      </div>
    )
  }
)
